import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';

/**
 * SectorDemoGallery — "Inside the demo" gallery used on each sector page.
 *
 * Groups of screenshots with captions and notes. Each tile is clickable and
 * opens a full-screen lightbox you can step through with the keyboard.
 */
const SectorDemoGallery = ({ folder, groups, sectorName, intro }) => {
  // Flatten all items for the lightbox to navigate through
  const flatItems = groups.flatMap((g) =>
    g.items.map((it) => ({ ...it, group: g.title })),
  );

  const [openIdx, setOpenIdx] = useState(null);
  const isOpen = openIdx !== null;

  const close = useCallback(() => setOpenIdx(null), []);
  const prev = useCallback(
    () => setOpenIdx((i) => (i === null ? null : (i - 1 + flatItems.length) % flatItems.length)),
    [flatItems.length],
  );
  const next = useCallback(
    () => setOpenIdx((i) => (i === null ? null : (i + 1) % flatItems.length)),
    [flatItems.length],
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, close, prev, next]);

  const active = isOpen ? flatItems[openIdx] : null;

  return (
    <section
      className="py-20 md:py-28 bg-white border-t border-slate-200"
      data-testid="sector-demo-gallery"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-14">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">
            Inside the demo
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">
            {sectorName} — real screens, real workflows.
          </h2>
          {intro && (
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">{intro}</p>
          )}
        </div>

        {groups.map((group, gIdx) => {
          // Calculate the running offset so we can map tile → flatItems index
          const offset = groups
            .slice(0, gIdx)
            .reduce((acc, g) => acc + g.items.length, 0);

          return (
            <div key={group.title} className="mb-16 last:mb-0">
              <div className="mb-6 max-w-3xl">
                <h3 className="text-xl md:text-2xl font-medium tracking-tight text-slate-900 mb-2">
                  {group.title}
                </h3>
                {group.desc && (
                  <p className="text-sm md:text-base text-slate-600 leading-relaxed">
                    {group.desc}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {group.items.map((item, iIdx) => {
                  const idx = offset + iIdx;
                  return (
                    <motion.button
                      type="button"
                      key={item.file}
                      initial={{ opacity: 0, y: 12 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: '0px 0px -50px 0px' }}
                      transition={{ delay: iIdx * 0.04 }}
                      onClick={() => setOpenIdx(idx)}
                      className="group text-left bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg hover:border-slate-300 transition-all"
                      data-testid={`demo-gallery-tile-${item.file.replace(/\.[^.]+$/, '')}`}
                    >
                      <div className="relative aspect-[16/10] bg-slate-100 overflow-hidden">
                        <img
                          src={`/images/${folder}/${item.file}`}
                          alt={item.caption}
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/10 transition-colors flex items-center justify-center">
                          <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/95 backdrop-blur-sm text-slate-900 text-xs font-medium px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 shadow">
                            <ZoomIn className="w-3.5 h-3.5" /> View full size
                          </span>
                        </div>
                      </div>
                      <div className="p-4 md:p-5">
                        <p className="text-sm font-medium text-slate-900">{item.caption}</p>
                        {item.note && (
                          <p className="mt-1.5 text-xs md:text-[13px] text-slate-600 leading-snug">
                            {item.note}
                          </p>
                        )}
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Lightbox */}
      {isOpen && active && (
        <div
          className="fixed inset-0 z-[120] bg-slate-950/95 backdrop-blur-sm flex flex-col"
          data-testid="demo-gallery-lightbox"
          onClick={close}
        >
          {/* Top bar */}
          <div
            className="flex items-center justify-between gap-4 px-4 md:px-8 py-4 text-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold">
                {active.group}
              </p>
              <p className="text-sm md:text-base font-medium truncate">{active.caption}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs text-slate-400 font-mono hidden sm:inline">
                {openIdx + 1} / {flatItems.length}
              </span>
              <button
                type="button"
                onClick={close}
                className="p-2 rounded-md text-slate-300 hover:text-white hover:bg-white/10 transition"
                aria-label="Close"
                data-testid="demo-gallery-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Image area */}
          <div className="flex-1 flex items-center justify-center px-2 md:px-12 pb-6 min-h-0">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                prev();
              }}
              className="hidden md:flex flex-shrink-0 p-3 rounded-full text-slate-300 hover:text-white hover:bg-white/10 transition mr-2"
              aria-label="Previous"
              data-testid="demo-gallery-prev"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            <img
              src={`/images/${folder}/${active.file}`}
              alt={active.caption}
              onClick={(e) => e.stopPropagation()}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            />

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                next();
              }}
              className="hidden md:flex flex-shrink-0 p-3 rounded-full text-slate-300 hover:text-white hover:bg-white/10 transition ml-2"
              aria-label="Next"
              data-testid="demo-gallery-next"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          {/* Mobile nav + caption */}
          <div
            className="md:hidden px-4 pb-6 text-white"
            onClick={(e) => e.stopPropagation()}
          >
            {active.note && (
              <p className="text-xs text-slate-300 mb-3 max-w-2xl">{active.note}</p>
            )}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={prev}
                className="px-4 py-2 rounded-md bg-white/10 hover:bg-white/15 inline-flex items-center gap-2 text-sm"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              <span className="text-xs text-slate-400 font-mono">
                {openIdx + 1} / {flatItems.length}
              </span>
              <button
                type="button"
                onClick={next}
                className="px-4 py-2 rounded-md bg-white/10 hover:bg-white/15 inline-flex items-center gap-2 text-sm"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Desktop caption */}
          {active.note && (
            <div
              className="hidden md:block max-w-3xl mx-auto px-8 pb-8 text-slate-300 text-sm text-center"
              onClick={(e) => e.stopPropagation()}
            >
              {active.note}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default SectorDemoGallery;
